import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const reasoningVariants = cva(
  "group rounded-lg border border-border/50 bg-muted/50 transition-all duration-150",
  {
    variants: {
      variant: {
        default: "",
        collapsed: "hover:bg-muted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface ReasoningProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof reasoningVariants> {
  isStreaming?: boolean;
  reasoningTime?: number;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  streamingStartTime?: number;
}

const Reasoning = React.forwardRef<HTMLDivElement, ReasoningProps>(
  (
    {
      className,
      variant,
      isStreaming = false,
      reasoningTime,
      defaultOpen = false,
      open: controlledOpen,
      onOpenChange,
      streamingStartTime,
      children,
      ...props
    },
    ref
  ) => {
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen || isStreaming);
    const hasAutoCollapsed = React.useRef(false);
    const collapseTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

    const setIsOpen = React.useCallback(
      (value: boolean) => {
        if (onOpenChange) {
          onOpenChange(value);
        } else {
          setInternalOpen(value);
        }
      },
      [onOpenChange]
    );

    React.useEffect(() => {
      if (isStreaming) {
        hasAutoCollapsed.current = false;
        setIsOpen(true);
      }
    }, [isStreaming, setIsOpen]);

    React.useEffect(() => {
      if (!isStreaming && !hasAutoCollapsed.current && controlledOpen === undefined) {
        if (collapseTimerRef.current) {
          clearTimeout(collapseTimerRef.current);
        }
        collapseTimerRef.current = setTimeout(() => {
          setIsOpen(false);
          hasAutoCollapsed.current = true;
        }, 1000);
      }
      return () => {
        if (collapseTimerRef.current) {
          clearTimeout(collapseTimerRef.current);
        }
      };
    }, [isStreaming, controlledOpen, setIsOpen]);

    const toggleOpen = () => {
      if (!isStreaming) {
        setIsOpen(!isOpen);
      }
    };

    const formatTime = (seconds: number) => {
      if (seconds < 60) {
        return `${seconds}s`;
      }
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    };

    const displayTime = React.useMemo(() => {
      if (reasoningTime !== undefined) {
        return reasoningTime;
      }
      if (isStreaming && streamingStartTime) {
        return Math.round((Date.now() - streamingStartTime) / 1000);
      }
      return undefined;
    }, [reasoningTime, isStreaming, streamingStartTime]);

    const [liveTime, setLiveTime] = React.useState<number | undefined>(displayTime);

    React.useEffect(() => {
      if (isStreaming && streamingStartTime && reasoningTime === undefined) {
        setLiveTime(Math.round((Date.now() - streamingStartTime) / 1000));
        const interval = setInterval(() => {
          setLiveTime(Math.round((Date.now() - streamingStartTime) / 1000));
        }, 1000);
        return () => clearInterval(interval);
      } else {
        setLiveTime(undefined);
      }
    }, [isStreaming, streamingStartTime, reasoningTime]);

    const effectiveDisplayTime = reasoningTime ?? liveTime;

    return (
      <div
        ref={ref}
        className={cn(reasoningVariants({ variant }), className)}
        {...props}
      >
        <button
          type="button"
          onClick={toggleOpen}
          disabled={isStreaming}
          className={cn(
            "flex w-full items-center justify-between px-4 py-3 text-sm font-medium",
            !isStreaming && "cursor-pointer hover:text-primary"
          )}
        >
          <div className="flex items-center gap-2">
            <svg
              className={cn(
                "h-4 w-4 transition-transform duration-150",
                isOpen && "rotate-90"
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span className="flex items-center gap-2">
              {isStreaming ? (
                <>
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  思考中...
                </>
              ) : (
                "思考过程"
              )}
            </span>
          </div>
          {effectiveDisplayTime !== undefined && (
            <span className="text-xs text-muted-foreground">
              {isStreaming ? "已思考" : "思考耗时"} {formatTime(effectiveDisplayTime)}
            </span>
          )}
        </button>
        <div
          className={cn(
            "overflow-hidden transition-all duration-150 ease-in-out",
            isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div className="px-4 pb-4 pt-0">
            <div className="rounded-md bg-background/50 p-3 text-sm text-muted-foreground">
              {children}
            </div>
          </div>
        </div>
      </div>
    );
  }
);
Reasoning.displayName = "Reasoning";

export { Reasoning, reasoningVariants };
